import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  ModalSizes,
  Form,
  FormSizes,
  Field,
  Dropdown,
  Button,
  ButtonVariants,
  ButtonSizes,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
  FormValidateOn,
} from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { useTranslations } from '@/hooks';
import {
  SummaryCard,
  ReferenceIdField,
  ShipmentQuantityField,
  DateOfDispatchField,
  VesselNameField,
} from '@/components/common';
import { buildAllocationDetailFields } from '../constants/allocationDetailFields';
import { InfoLabel } from './InfoLabel';
import { allocationsValidation, buildShipmentQuantityRules } from '../allocationsValidation';
import { buildRowUnitOptions } from '../constants/allocationUnits';
import styles from './UpdateShipmentDetailsModal.module.scss';

const SHIPMENT_ID_PREFIX = 'SP';

// Each card owns a slice of the flat form store, keyed by the row's id.
const rowKey = (id, field) => `shipment_${id}_${field}`;

// The card's slice of the store, unprefixed — what the save sends per shipment.
const readRow = (values, id) => ({
  idPrefix: values[rowKey(id, 'idPrefix')],
  idNumber: values[rowKey(id, 'idNumber')],
  quantity: values[rowKey(id, 'quantity')],
  unitType: values[rowKey(id, 'unit')],
  dateOfDispatch: values[rowKey(id, 'dateOfDispatch')],
  vesselName: values[rowKey(id, 'vesselName')],
});

const buildRowValues = (id) => ({
  [rowKey(id, 'idPrefix')]: SHIPMENT_ID_PREFIX,
  [rowKey(id, 'idNumber')]: '',
  [rowKey(id, 'quantity')]: '',
  [rowKey(id, 'unit')]: '',
  [rowKey(id, 'dateOfDispatch')]: '',
  [rowKey(id, 'vesselName')]: '',
});

/**
 * Props:
 *   id          the row's key in the flat form store
 *   index       zero-based position, for the "Shipment - N" heading
 *   unitOptions working / declaration unit choices
 *   rawByUnit   the unformatted certified quantities the row's quantity is capped against
 *   onRemove    (id) => void
 *   setValue    the Form's own setter, for the Generate button
 */
const ShipmentCard = ({ id, index, unitOptions, rawByUnit, onRemove, setValue }) => {
  const t = useTranslations();
  const shipmentQuantityLabel = useMemo(
    () => <InfoLabel>{t('FIELD_SHIPMENT_QUANTITY')}</InfoLabel>,
    [t],
  );
  const unitLabel = useMemo(() => <InfoLabel>{t('FIELD_UNIT')}</InfoLabel>, [t]);

  const handleRemove = useCallback(() => onRemove(id), [onRemove, id]);
  const handleGenerate = useCallback(
    (number) => setValue(rowKey(id, 'idNumber'), number),
    [setValue, id],
  );
  const quantityRules = useMemo(
    () => buildShipmentQuantityRules(rawByUnit, rowKey(id, 'unit')),
    [rawByUnit, id],
  );

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <Typography
          variant={TypographyVariants.bodySm}
          weight={TypographyWeights.semibold}
          color={TypographyColors.primary}
        >
          {t('ALLOCATIONS_SHIPMENT_CARD_TITLE', { index: index + 1 })}
        </Typography>
        <Button variant={ButtonVariants.tertiary} size={ButtonSizes.sm} onClick={handleRemove}>
          {t('COMMON_REMOVE')}
        </Button>
      </div>

      <div className={styles.cardBody}>
        <Form.Row columns={1}>
          <ReferenceIdField
            label={t('FIELD_SHIPMENT_ID')}
            namePrefix={`shipment_${id}_`}
            rules={allocationsValidation.shipmentId}
            onGenerate={handleGenerate}
          />
        </Form.Row>

        <Form.Row columns={2}>
          <ShipmentQuantityField
            fieldName={rowKey(id, 'quantity')}
            rules={quantityRules}
            label={shipmentQuantityLabel}
            requiredMessage={t('ALLOCATIONS_SHIPMENT_QUANTITY_REQUIRED')}
            placeholder={t('ALLOCATIONS_SHIPMENT_QUANTITY_PLACEHOLDER')}
          />

          <Field name={rowKey(id, 'unit')} rules={allocationsValidation.unit}>
            {({ id: inputId, name, value, onChange, onBlur, status, message }) => (
              <Dropdown
                id={inputId}
                name={name}
                label={unitLabel}
                placeholder={t('ALLOCATIONS_SHIPMENT_UNIT_PLACEHOLDER')}
                options={unitOptions}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                status={status}
                message={message}
                required
              />
            )}
          </Field>
        </Form.Row>

        <Form.Row columns={2}>
          <DateOfDispatchField
            fieldName={rowKey(id, 'dateOfDispatch')}
            rules={allocationsValidation.dateOfDispatch}
          />

          <VesselNameField fieldName={rowKey(id, 'vesselName')} />
        </Form.Row>
      </div>
    </div>
  );
};

/**
 * Props:
 *   open        whether the modal is shown
 *   onClose     ✕ / Cancel
 *   allocation  the allocation detail view model
 *   isSaving    disables Save while the mutation is in flight
 *   onSubmit    (shipments) => void — one entry per card; runs only once validation passes
 */
export const UpdateShipmentDetailsModal = ({ open, onClose, allocation, isSaving, onSubmit }) => {
  const t = useTranslations();
  const addIcon = useMemo(() => <Plus />, []);
  const [rowIds, setRowIds] = useState([0]);
  // The Modal keeps its children mounted once opened, so only a fresh key clears the last edit.
  const [session, setSession] = useState(0);

  useEffect(() => {
    if (!open) return;
    setRowIds([0]);
    setSession((previous) => previous + 1);
  }, [open]);

  const detailFields = useMemo(() => buildAllocationDetailFields(t, allocation), [t, allocation]);

  // Figma names the option by the unit symbol and badges the basis — the cells' builder, swapped.
  const unitOptions = useMemo(
    () =>
      buildRowUnitOptions(t, allocation?.byUnit).map((option) => ({
        value: option.value,
        label: option.badgeText,
        badgeText: option.label,
      })),
    [t, allocation?.byUnit],
  );

  const initialValues = useMemo(
    () => rowIds.reduce((values, id) => Object.assign(values, buildRowValues(id)), {}),
    [rowIds],
  );

  const handleAddRow = useCallback(
    () => setRowIds((ids) => [...ids, (ids[ids.length - 1] ?? 0) + 1]),
    [],
  );
  const handleRemoveRow = useCallback(
    (id) => setRowIds((ids) => ids.filter((rowId) => rowId !== id)),
    [],
  );

  const handleSubmit = useCallback(
    (values) => onSubmit?.(rowIds.map((id) => readRow(values, id))),
    [onSubmit, rowIds],
  );

  // Modal owns the footer, so Save reaches the form through the submit it hands the render prop.
  const submitRef = useRef(null);
  const handleSave = useCallback(() => submitRef.current?.(), []);

  const buttons = useMemo(
    () => [
      { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: onClose },
      {
        label: t('COMMON_SAVE'),
        variant: ButtonVariants.primary,
        onClick: handleSave,
        disabled: isSaving,
      },
    ],
    [t, onClose, handleSave, isSaving],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size={ModalSizes.large}
      title={t('ALLOCATIONS_UPDATE_SHIPMENT_TITLE')}
      buttons={buttons}
    >
      <Form
        validateOn={FormValidateOn.change}
        key={session}
        className={styles.form}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        size={FormSizes.large}
      >
        {({ setValue, submit }) => {
          submitRef.current = submit;
          return (
            <>
              <SummaryCard
                heading={t('ALLOCATIONS_SECTION_ALLOCATION_DETAILS')}
                fields={detailFields}
                columns={3}
              />

              <div className={styles.intro}>
                <Typography
                  variant={TypographyVariants.bodySm}
                  weight={TypographyWeights.semibold}
                  color={TypographyColors.primary}
                >
                  {t('SECTION_SHIPPING_DETAILS_OPTIONAL')}
                </Typography>
                <Typography variant={TypographyVariants.bodyXs} color={TypographyColors.secondary}>
                  {t('ALLOCATIONS_SHIPPING_DETAILS_DESCRIPTION')}
                </Typography>
              </div>

              {rowIds.map((id, index) => (
                <ShipmentCard
                  key={id}
                  id={id}
                  index={index}
                  unitOptions={unitOptions}
                  rawByUnit={allocation?.rawByUnit}
                  onRemove={handleRemoveRow}
                  setValue={setValue}
                />
              ))}

              <Button
                variant={ButtonVariants.secondary}
                size={ButtonSizes.sm}
                leftIcon={addIcon}
                onClick={handleAddRow}
                fullWidth
              >
                {t('ALLOCATIONS_ADD_MORE_SHIPMENTS')}
              </Button>
            </>
          );
        }}
      </Form>
    </Modal>
  );
};
